import {
    bindDialogDismiss,
    whenCbarDialogReady,
    type CbarDialogElement,
} from './cbar-dialog-helpers';
import {
    DOCUMENT_PREVIEW_CONTENT_CLASS,
    bindDocumentPreviewFit,
    fitDocumentPreviewInDialog,
} from './document-preview-dialog';
import {runOnce} from './run-once';

type InvoiceWidgetResponse = {
    result: string;
    widget?: string;
    actions?: string;
    error?: string;
};

let dialog: CbarDialogElement | null = null;
let contentEl: HTMLElement | null = null;
let actionsEl: HTMLElement | null = null;
let loadInFlight: string | null = null;

function getDialog(): CbarDialogElement {
    if (dialog) {
        return dialog;
    }

    dialog = document.createElement('cbar-dialog') as CbarDialogElement;
    dialog.id = 'invoice-preview-dialog';
    dialog.className = 'invoice-preview-dialog document-preview-dialog';

    const headlineEl = document.createElement('div');
    headlineEl.slot = 'headline';
    headlineEl.className = 'invoice-preview-dialog__headline';
    headlineEl.textContent = 'Invoice preview';

    contentEl = document.createElement('div');
    contentEl.slot = 'content';
    contentEl.className = `invoice-preview-dialog__content ${DOCUMENT_PREVIEW_CONTENT_CLASS}`;

    actionsEl = document.createElement('div');
    actionsEl.slot = 'actions';
    actionsEl.className = 'invoice-preview-dialog__actions';

    dialog.append(headlineEl, contentEl, actionsEl);
    document.body.appendChild(dialog);
    bindDialogDismiss(dialog);
    bindDocumentPreviewFit(dialog);

    return dialog;
}

function showLoading(): void {
    if (!contentEl) {
        return;
    }

    contentEl.replaceChildren();
    const wrap = document.createElement('div');
    wrap.className = 'cbar-loading-indicator';
    wrap.setAttribute('aria-label', 'Loading invoice');
    const spinner = document.createElement('cbar-circular-progress');
    (spinner as HTMLElement & {indeterminate: boolean}).indeterminate = true;
    wrap.appendChild(spinner);
    contentEl.appendChild(wrap);

    if (actionsEl) {
        actionsEl.replaceChildren();
    }
}

async function fetchInvoiceWidget(invoiceId: string): Promise<InvoiceWidgetResponse> {
    const response = await fetch(`/ajax/loadinvoicewidget/id/${encodeURIComponent(invoiceId)}`);
    return response.json() as Promise<InvoiceWidgetResponse>;
}

async function openInvoicePreview(invoiceId: string): Promise<void> {
    await whenCbarDialogReady();

    const dlg = getDialog();
    showLoading();
    dlg.open = true;

    loadInFlight = invoiceId;
    try {
        const data = await fetchInvoiceWidget(invoiceId);
        if (loadInFlight !== invoiceId) {
            return;
        }

        if (data.result !== 'success' || !data.widget) {
            if (contentEl) {
                contentEl.textContent = data.error ?? 'Unable to load invoice';
            }
            return;
        }

        if (contentEl) {
            contentEl.innerHTML = data.widget;
        }
        if (actionsEl) {
            actionsEl.innerHTML = data.actions ?? '';
            bindDialogDismiss(dlg);
        }

        requestAnimationFrame(() => {
            fitDocumentPreviewInDialog(dlg);
        });
    } catch (err) {
        if (loadInFlight !== invoiceId || !contentEl) {
            return;
        }
        contentEl.textContent = err instanceof Error ? err.message : 'Unable to load invoice';
    } finally {
        if (loadInFlight === invoiceId) {
            loadInFlight = null;
        }
    }
}

function findInvoicePreviewTrigger(event: Event): Element | null {
    const target = event.target as Element | null;
    const closest = target?.closest?.('.invoice-preview-trigger') ?? null;
    if (closest) {
        return closest;
    }

    const path = event.composedPath?.() ?? [event.target as EventTarget];
    for (const node of path) {
        const el = node as Element;
        if (el?.classList?.contains('invoice-preview-trigger')) {
            return el;
        }
    }

    return null;
}

function bindInvoicePreviewTriggers(): void {
    document.addEventListener('click', (event) => {
        const trigger = findInvoicePreviewTrigger(event);
        if (!trigger) {
            return;
        }

        event.preventDefault();

        const invoiceId = trigger.getAttribute('data-invoice-id');
        if (!invoiceId) {
            return;
        }

        void openInvoicePreview(invoiceId);
    });
}

export function initInvoicePreview(): void {
    runOnce('invoice-preview', () => {
        bindInvoicePreviewTriggers();
    });
}
